import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRightRounded';
import {
  useFormContext,
} from 'react-hook-form';
import clsx from 'clsx';
import {
  Dayjs,
} from 'utils/dayjs';
import {
  formatDateOnly,
} from 'utils/date';
import ToolTip from 'core/components/Tooltip';
import IconButton from '@mui/material/IconButton';
import {
  AppointmentForm,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/index';

function getWeekWindow(selectedDate: Dayjs, weekOffset: number) {
  const startOfWeek = selectedDate?.startOf('week');
  const mondayStart = startOfWeek?.add(1, 'day');
  // If selectedDate is Sunday, step back one week so it stays visible
  const windowStart = (selectedDate?.day() === 0 ? mondayStart?.subtract(7, 'day') : mondayStart)
    ?.add(weekOffset * 7, 'day');
  const windowEnd = windowStart?.add(6, 'day');

  const days = Array.from({ length: 7 }, (_, index) => windowStart?.add(index, 'day'));

  const weekLabel = windowStart && windowEnd
    ? `${windowStart.format('MMM D')} – ${windowEnd.format('MMM D, YYYY')}`
    : '';

  return { weekLabel, days };
}

export default function WeekDateStrip() {
  const { setValue, getValues, watch } = useFormContext<AppointmentForm>();
  const eventDateTime = watch('eventDateTime');
  const selectedDate = eventDateTime?.startTime;
  const [weekOffset, setWeekOffset] = useState(0);

  const {
    days,
    weekLabel,
  } = useMemo(
    () => getWeekWindow(selectedDate, weekOffset),
    [selectedDate, weekOffset, eventDateTime],
  );

  useEffect(() => {
    setWeekOffset(0);
  }, [selectedDate]);

  function onDayClick(day: Dayjs) {
    const currentValue = getValues('eventDateTime');
    const { startTime } = currentValue ?? {};

    if (startTime?.isValid()) {
      const newStartTime = day
        .hour(startTime.hour())
        .minute(startTime.minute());

      setValue('eventDateTime', {
        ...currentValue,
        startTime: newStartTime,
      });
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-gray-800">{weekLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          <ToolTip title="Previous week">
            <div>
              <IconButton
                size="small"
                onClick={() => {
                  setWeekOffset((prev) => prev - 1);
                }}
                className="!text-gray-500 !rounded-full !p-0.5"
              >
                <ChevronLeftIcon className="!w-5 !h-5" />
              </IconButton>
            </div>
          </ToolTip>
          <ToolTip title="Next week">
            <div>
              <IconButton
                size="small"
                onClick={() => {
                  setWeekOffset((prev) => prev + 1);
                }}
                className="!text-gray-500 !rounded-full !p-0.5"
              >
                <ChevronRightIcon className="!w-5 !h-5" />
              </IconButton>
            </div>
          </ToolTip>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-3 bg-gray-50 border border-gray-100 rounded p-2">
        {(days.filter(Boolean) as Dayjs[]).map((day) => {
          const isSelected = selectedDate?.isSame(day, 'day');
          return (
            <button
              type="button"
              key={formatDateOnly(day)}
              onClick={() => onDayClick(day)}
              className={clsx(
                'flex flex-col items-center justify-center py-1.5 rounded-md transition-colors',
                {
                  'bg-secondary text-white': isSelected,
                  'text-gray-600 bg-white hover:bg-gray-100 border border-gray-200': !isSelected,
                },
              )}
            >
              <span className="text-[10px] font-medium uppercase  leading-none">{day.format('ddd')}</span>
              <span className="text-base font-semibold leading-none  mt-0.5">{day.format('D')}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
